"use client";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useRouter } from "next/navigation";

import { emailSchema } from "@/lib/zod";
import { handleApiError } from "@/utils/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Form } from "react-bootstrap";
import { z } from "zod";

import AuthButton from "./auth-button";
import FloatingInput from "./floating-input";

import api, { ApiError } from "@/lib/api";

type FormFields = z.infer<typeof emailSchema>;

export default function ResetPasswordForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(emailSchema),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!executeRecaptcha) {
      setError("root", {
        type: "manual",
        message: "reCAPTCHA not ready or failed to load.",
      });
      return;
    }

    try {
      const token = await executeRecaptcha("reset_password");

      await api.post("/users/reset_password/", {
        ...data,
        recaptcha: token,
      });

      toast.success(
        "If we have an account associated with the provided email address, we will send password reset instructions shortly. Please check your email and follow the instructions to reset your password.",
      );
      toast.success(
        "If you don't see the email, check your spam or junk folder. Ensure the email address you entered is correct.",
      );

      router.push("/");
    } catch (error) {
      if (error instanceof ApiError) {
        handleApiError(error, setError);
      } else {
        setError("root", {
          type: "manual",
          message: "An unexpected error occurred while validating the reCAPTCHA. Please try again later.",
        });
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FloatingInput type="email" label="Email Address" register={register("email")} error={errors.email} />
      {errors.root && <Alert variant="danger">{errors.root.message}</Alert>}
      <AuthButton title="Reset password" isLoading={isSubmitting} />
    </Form>
  );
}
